import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { authApi } from '../services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  initializeAuth: () => void;
  // Helper methods for client-based permissions
  isCompanyStaff: () => boolean;
  isClientUser: () => boolean;
  isAdmin: () => boolean;
  canAccessAllClients: () => boolean;
  getClientId: () => number | null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.login({ email, password });
          
          if (response.success && response.data) {
            const { user, token } = response.data;
            set({ 
              user, 
              token, 
              isLoading: false, 
              error: null 
            });
            
            // Set axios default authorization header
            if (typeof window !== 'undefined') {
              localStorage.setItem('auth_token', token);
            }
            
            return true;
          } else {
            set({ 
              isLoading: false, 
              error: response.message || 'Login failed' 
            });
            return false;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          set({ 
            isLoading: false, 
            error: errorMessage 
          });
          return false;
        }
      },

      logout: () => {
        set({ 
          user: null, 
          token: null, 
          error: null 
        });
        
        // Clear stored token
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth_token');
        }
      },

      clearError: () => {
        set({ error: null });
      },

      initializeAuth: () => {
        set({ isLoading: true });
        
        // Check if token exists in localStorage
        if (typeof window !== 'undefined') {
          const token = localStorage.getItem('auth_token');
          if (token) {
            // Verify token with backend
            authApi.verifyToken(token)
              .then((response) => {
                if (response.success && response.data) {
                  set({ 
                    user: response.data, 
                    token, 
                    isLoading: false 
                  });
                } else {
                  // Token is invalid, clear it
                  localStorage.removeItem('auth_token');
                  set({ 
                    user: null, 
                    token: null, 
                    isLoading: false 
                  });
                }
              })
              .catch(() => {
                // Token verification failed, clear it
                localStorage.removeItem('auth_token');
                set({ 
                  user: null, 
                  token: null, 
                  isLoading: false 
                });
              });
          } else {
            set({ isLoading: false });
          }
        } else {
          set({ isLoading: false });
        }
      },

      // Helper methods for client-based permissions
      isCompanyStaff: () => {
        const { user } = get();
        return !!user && !user.client_id; // Company staff have null client_id
      },

      isClientUser: () => {
        const { user } = get();
        return !!user && !!user.client_id; // Client users have a client_id
      },

      isAdmin: () => {
        const { user } = get();
        return !!user && (user.role === 'admin' || user.role === 'manager');
      },

      canAccessAllClients: () => {
        const { user } = get();
        return !!user && !user.client_id && (user.role === 'admin' || user.role === 'manager' || user.role === 'dispatcher');
      },

      getClientId: () => {
        const { user } = get();
        return user?.client_id || null;
      },
    }),
    {
      name: 'fleet-auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token 
      }),
    }
  )
);